<template>
  <div>
    <div
      id="validation-errors-box"
      class="validation-errors-box"
      v-if="validationErrors && validationErrors.length > 0"
    >
      <template v-for="error in validationErrors">
        <span class="validation-error" :key="error">
          {{ $t("components.plan_builder." + error) }}
        </span>
      </template>
    </div>
    <div class="with-margin">
      <v-card-text>
        {{
          $t(
            "components.plancreationwizard.you_are_few_steps_away_from_building_a_new_benefits_plan"
          )
        }}
      </v-card-text>
      <v-expansion-panels
        accordion
        focusable
        multiple
        v-model="panels"
        :disabled="loading"
      >
        <v-expansion-panel
          id="plan_creation_step1"
          @change="panelFocus('plan_creation_step1')"
        >
          <v-expansion-panel-header
            :class="
              !validated || valid.generalSettings
                ? ''
                : 'validation-error-header'
            "
          >
            {{ $t("components.plancreationwizard.general_settings") }}
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <v-card-subtitle>
              {{
                $t(
                  "components.plan_builder.pensions_plan_builder.in_this_section_configure"
                )
              }}
            </v-card-subtitle>
            <v-card-text>
              <general-settings
                :generalSettings="generalSettings"
                @valid="valid.generalSettings = $event"
                @trust-fund="$emit('trust-fund', $event)"
                :disabled="loading"
                :planType="planType"
                @has-classes="
                  $emit('has-classes', $event);
                "
                @has-occupations="
                  $emit('has-occupations', $event);
                "
                @update-classes="
                  classesStr = $event;
                  $emit('update-classes', $event);
                "
                @update-occupations="
                  occupationsStr = $event;
                  $emit('update-occupations', $event);
                "
                @update-coverage="
                  $emit('change-coverage', $event);
                "
                :organizationId="organizationId"
        :draftId="draftId"
        :coverages="coverage"
        @load-unique-identifiers="$emit('load-unique-identifiers')"
                :uniqueIdentifiers="uniqueIdentifiers"
              />
            </v-card-text>
          </v-expansion-panel-content>
        </v-expansion-panel>
        <v-expansion-panel
          id="plan_creation_step2"
          @change="panelFocus('plan_creation_step2')"
        >
          <v-expansion-panel-header
            :class="
              !validated || valid.unionsLocals ? '' : 'validation-error-header'
            "
          >
            {{ $t("components.plancreationwizard.unions_locals") }}
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <v-card-subtitle>
              {{
                $t(
                  "components.plan_builder.pensions_plan_builder.configure_unions_and_locals"
                )
              }}
            </v-card-subtitle>
            <v-card-text>
              <unions-locals
                :unions="unions"
                @unions-changed="$emit('unions-changed', $event)"
                :disabled="loading"
                :planIdentityId="planId"
              />
            </v-card-text>
          </v-expansion-panel-content>
        </v-expansion-panel>
        <v-expansion-panel
          v-if="generalSettings.PlanSettings.IncludeTrustFund"
          id="plan_creation_step3"
          @change="panelFocus('plan_creation_step1')"
        >
          <v-expansion-panel-header
            :class="
              !validated || (valid.trustFund && bankAccountValid) ? '' : 'validation-error-header'
            "
          >
            {{ $t("components.plancreationwizard.trust_fund") }}
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <v-card-subtitle>
              {{
                $t(
                  "components.plancreationwizard.select_and_or_add_plan_trust_fund_and_their_information"
                )
              }}
            </v-card-subtitle>
            <v-card-text>
              <trust-fund
                :trustFund="trustFund"
                :planIdentityId="planId"
                @valid="valid.trustFund = $event"
                :disabled="loading"
                @trust-fund="$emit('trust-fund', $event)"
                :planEffectiveDate="generalSettings.PlanSettings.EffectiveDate"
                :hasBeneficiaries="
                  generalSettings.PlanSettings.HasBeneficiaries
                "
                :bankAccount="bankAccount"
                @bank-account="$emit('bank-account', $event)"
                ref="trustFund"
              />
            </v-card-text>
          </v-expansion-panel-content>
        </v-expansion-panel>
        <v-expansion-panel
          id="plan_creation_step4"
          @change="panelFocus('plan_creation_step4')"
        >
          <v-expansion-panel-header
            :class="
              !validated || valid.employers ? '' : 'validation-error-header'
            "
          >
            {{ $t("components.plancreationwizard.employers_&_stores") }}
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <v-card-subtitle>
              {{
                $t(
                  "components.plancreationwizard.add_employers_and_stores_and_their_information"
                )
              }}
            </v-card-subtitle>
            <v-card-text>
              <employers
                ref="inMemoryEmployers"
                :employers="employers"
                @change-employers="changeEmployers"
                :planIdentityId="planId"
                :hasBillingImport="
                  generalSettings.PlanSettings.HasBillingImport
                "
                :disabled="loading"
              ></employers>
            </v-card-text>
          </v-expansion-panel-content>
        </v-expansion-panel>
        <v-expansion-panel id="plan_creation_step5" @change="panelFocus('plan_creation_step5')">
          <v-expansion-panel-header
            :class="
              !validated || valid.memberFields ? '' : 'validation-error-header'
            "
          >
            {{
              $t("components.plancreationwizard.members_data_fields_&_flags")
            }}
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <v-card-subtitle>
              {{
                $t(
                  "components.plancreationwizard.select_members_data_fields_and_add_and_configure_flags_for_member_management"
                )
              }}
            </v-card-subtitle>
            <v-card-text>
              <member-fields
                :selectedFields="selectedFields"
                :unselectedFields="unselectedFields"
                @update-selection="changeMemberFields"
                :disabled="loading"
              />
              <hour-import-member-identifiers
                v-if="generalSettings.TrackHours"
                :primaryIdentifier="primaryIdentifier"
                :candidateIdentifiers="candidateIdentifiers"
                :includeContributionAmount="includeContributionAmount"
                @update-primary-identifier="
                  $emit('update-primary-identifier', $event)
                "
                @update-candidate-identifiers="
                  $emit('update-candidate-identifiers', $event)
                "
                @update-include-contribution-amount="
                  $emit('update-include-contribution-amount', $event)
                "
                :selectedFields="selectedFields"
                :disabled="loading"
              />
              <flags
                :planIdentityId="planId"
                :flags="flags"
                @update-flags="$emit('change-flags', $event)"
                :disabled="loading"
              />
            </v-card-text>
          </v-expansion-panel-content>
        </v-expansion-panel>
        <v-expansion-panel
          v-if="generalSettings.PlanSettings.HasBilling"
          id="plan_creation_step6"
          @change="panelFocus('plan_creation_step6')"
        >
          <v-expansion-panel-header
            :class="
              !validated || valid.billingImport ? '' : 'validation-error-header'
            "
          >
            {{ $t("components.plancreationwizard.billing_import") }}
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <v-card-subtitle>
              {{generalSettings.PlanSettings.HasBillingImport?$t("components.plancreationwizard.configure_billing_import"):$t("components.plancreationwizard.no_billing_import") }}
            </v-card-subtitle>
            <v-card-text>
              <v-container fluid grid-list-md>
                <billing-import
                  :selectedFields="billingFields"
                  :unselectedFields="unselectedBillingFields"
                  @update-selection="$emit('change-billing-fields', $event)"
                  :effectiveDate="generalSettings.PlanSettings.EffectiveDate"
                  @update-billing-term="$emit('change-billing-term', $event)"
                  :billingTerm="billingTerm"
                  :employers="employers"
                  :hasBillingImport="
                    generalSettings.PlanSettings.HasBillingImport
                  "
                  :disabled="loading"
                  @update-contribution-categories="
                    $emit('change-contribution-categories', $event)
                  "
                  :contributionCategories="contributionCategories"
                  :hasStores="generalSettings.PlanSettings.HasStores"
                />
              </v-container>
            </v-card-text>
          </v-expansion-panel-content>
        </v-expansion-panel>
        <v-expansion-panel
          id="plan_creation_step9"
          @change="panelFocus('plan_creation_step9')"
        >
          <v-expansion-panel-header
            :class="
              !validated || valid.coverageParameters
                ? ''
                : 'validation-error-header'
            "
          >
            {{ $t("components.plancreationwizard.coverage_configuration") }}
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <v-card-subtitle>
              {{
                $t(
                  "components.plancreationwizard.create_coverage_configuration_parameters"
                )
              }}
            </v-card-subtitle>
            <v-card-text>
              <v-container fluid>
                <coverage-parameters
                  :planIdentityId="planId"
                  :coverageParameters="coverageParameters"
                  @update-coverage-parameters="
                    $emit('change-coverage-parameters', $event)
                  "
                  :coverage="coverage"
                  :operatingFeesPerTerm="
                    generalSettings.PlanSettings.OperatingFeesPerTerm
                  "
                  :adminFeesPerTerm="
                    generalSettings.PlanSettings.AdminFeesPerTerm
                  "
                  :disabled="loading"
                  :contributionCategories="contributionCategories"
                  :employers="employers"
                  :planType="planType"
                  :calculationEntitiesFormulaTypes="calculationEntitiesFormulaTypes"
                />
              </v-container>
            </v-card-text>
          </v-expansion-panel-content>
        </v-expansion-panel>
        <v-expansion-panel
          id="plan_creation_step11"
          @change="panelFocus('plan_creation_step11')"
        >
          <v-expansion-panel-header
            :class="
              !validated || valid.memberGroups ? '' : 'validation-error-header'
            "
          >
            {{ $t("globals.member_groups") }}
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <v-card-subtitle>
              {{
                $t(
                  "components.plancreationwizard.create_and_configure_member_groups"
                )
              }}
            </v-card-subtitle>
            <v-card-text>
              <v-container fluid>
                <member-groups
                  ref="memberGroupsTable"
                  :memberGroups="memberGroups"
                  :coverage="coverage"
                  identifier="inMemoryMemberGroups"
                  :planIdentityId="planId"
                  @update-member-groups="$emit('change-groups', $event)"
                  :disabled="loading"
                ></member-groups>
              </v-container>
            </v-card-text>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>
      <v-snackbar
        v-model="snackbar"
        :color="color"
        :multi-line="mode === 'multi-line'"
        :timeout="timeout"
        :vertical="mode === 'vertical'"
      >
        {{ message }}
        <v-btn dark text @click="snackbar = false">
          {{ $t("globals.close") }}
        </v-btn>
      </v-snackbar>
      <error-handler :error="error"></error-handler>
    </div>
  </div>
</template>

<script>
import ErrorHandler from "@/components/ErrorHandler";
import { locker } from "@/mixins/locker";
import { mapGetters, mapActions } from "vuex";
import { tokens, mask } from "vue-the-mask";
import masker from "vue-the-mask/src/masker";
import GeneralSettings from "@/components/PlanBuilder/GeneralSettings.vue";
import UnionsLocals from "@/components/PlanBuilder/UnionsLocals.vue";
import Employers from "@/components/PlanBuilder/Employers.vue";
import TrustFund from "@/components/PlanBuilder/TrustFund.vue";
import MemberFields from "@/components/PlanBuilder/MemberFields.vue";
import MemberGroups from "@/components/PlanBuilder/MemberGroups.vue";
import Flags from "@/components/PlanBuilder/Flags.vue";
import BillingImport from "@/components/PlanBuilder/BillingImport.vue";
import HourImportMemberIdentifiers from "@/components/PlanBuilder/HourImportMemberIdentifiers.vue";
import CoverageParameters from "@/components/PlanBuilder/CoverageParameters.vue";

export default {
  components: {
    ErrorHandler,
    GeneralSettings,
    UnionsLocals,
    Employers,
    TrustFund,
    MemberFields,
    MemberGroups,
    Flags,
    BillingImport,
    HourImportMemberIdentifiers,
    CoverageParameters
  },
  mixins: [locker],
  directives: { mask },
  props: [
    "generalSettings",
    "planId",
    "validationErrors",
    "panelsWithErrors",
    "validated",
    "unions",
    "planType",
    "employers",
    "trustFund",
    "selectedFields",
    "bankAccount",
    "draftId",
    "organizationId",
    "coverage",
    "memberGroups",
    "flags",
    "billingFields",
    "billingTerm",
    "contributionCategories",
    "primaryIdentifier",
    "candidateIdentifiers",
    "includeContributionAmount",
    "coverageParameters",
    "calculationEntitiesFormulaTypes",
    "memberFields",
    "uniqueIdentifiers",
    "hasClasses",
    "hasOccupations"
  ],
  data() {
    return {
      panels: [0, 1, 2, 3, 4, 5, 6, 7],
      // panels: [],
      postalCodeMask: "A#A #A#",
      telephoneMask: "(###) ### - ####",
      systemEffectiveDate: null,
      // rules: {
      //   phone: (v) => {
      //     let val = this.unmask(v, this.telephoneMask);
      //     return !val || this.$rules.phone(val);
      //   },
      // },
      valid: {
        generalSettings: true,
        unionsLocals: true,
        employers: true,
        trustFund: true,
        memberFields: true,
        bankAccount: true,
        memberGroups:true,
        billingImport: true,
        coverageParameters: true,
      },
      loaded: false,
      error: null,
      snackbar: false,
      color: "",
      mode: "",
      timeout: 2500,
      message: "",
      loading: false,
      unselectedFields: [],
      bankAccountValid: true,
      allBillingImportFields: null,
      unselectedBillingFields: [],
      classesStr: "",
      occupationsStr: "",
    };
  },
  computed: {
    ...mapGetters("plansMembers", ["options"]),
  },
  watch: {
    billingFields: {
      handler(v) {
        if (v !== null && v !== undefined && this.allBillingImportFields ) {
          this.unselectedBillingFields = this.allBillingImportFields.filter(
            (o) =>
              v.filter((d) => d.Key === o.Key).length === 0
          );
        }
      },
      immediate: true,
    },
    valid: {
      handler(v) {
        this.validate();
      },
      immediate: true,
      deep: true,
    },
    generalSettings: {
      handler(v) {
        if (v && v.PlanSettings) {
          this.systemEffectiveDate = v.PlanSettings.EffectiveDate;
          this.generalSettings.PlanSettings.BeneficiaryStatementOnFund = true;
          this.generalSettings.PlanSettings.HasBeneficiaries = true;
          this.generalSettings.TrackHours = true;
        }
      },
    },
    "valid.trustFund": {
      handler(v) {
        console.log("valid.trustfund", v);
      }
    },
    "generalSettings.TrackHours": {
      handler(v) {
        if (!v) {
          this.$emit("update-primary-identifier", undefined);
          this.$emit("update-candidate-identifiers", []);
        }
      },
    },
    memberFields: {
      handler(v) {
        if (v) {
          this.unselectedFields = v
            .filter(
              (o) =>
                this.selectedFields.filter((f) => f.Key === o.Key).length === 0
            )
            .sort(this.compareFields);
        }
      },
    },
    hasClasses: {
      handler(v) {
        this.computeUnselectedMemberFields();
      },
    },
    hasOccupations: {
      handler(v) {
        this.computeUnselectedMemberFields();
      },
    },
    allBillingImportFields: {
      handler(v) {
        this.unselectedBillingFields = this.allBillingImportFields.filter(
          (o) => this.billingFields.filter((d) => d.Key === o.Key).length === 0
        );
      },
    },
  },
  async mounted() {
    if (this.generalSettings && this.generalSettings.PlanSettings) {
      this.systemEffectiveDate = this.generalSettings.PlanSettings.EffectiveDate;
    }
    if (this.allBillingImportFields && this.billingFields) {
      this.unselectedBillingFields = this.allBillingImportFields.filter(
        (o) => this.billingFields.filter((d) => d.Key === o.Key).length === 0
      );
    }
    
    this.generalSettings.TrackHours = true;
    this.generalSettings.PlanSettings.BeneficiaryStatementOnFund = true;
    this.generalSettings.PlanSettings.HasBeneficiaries = true;

    this.$nextTick(() => {
      this.panels = [];
    });
  },
  methods: {
    ...mapActions("plansMembers", ["getMemberFields","validateBankAccount", "getBillingImportFields"]),
    computeUnselectedMemberFields() {
      // let memberFields = JSON.parse(JSON.stringify(this.memberFields));

      // if (!this.hasClasses) {
      //   memberFields = memberFields.filter((o) => o.Key !== "Class");
      // } else {
      //   if (
      //     this.selectedFields.filter((o) => o.Key === "Class").length === 0
      //   ) {
      //     let field = memberFields.filter((o) => o.Key === "Class")[0];
      //     if (field !== undefined && field !== null) {
      //       this.selectedFields.push(field);
      //     }
      //   }
      // }
      // if (!this.hasOccupations) {
      //   memberFields = memberFields.filter((o) => o.Key !== "Occupation");
      // } else {
      //   if (
      //     this.selectedFields.filter((o) => o.Key === "Occupation").length ===
      //     0
      //   ) {
      //     let field = memberFields.filter((o) => o.Key === "Occupation")[0];
      //     this.selectedFields.push(field);
      //   }
      // }

      this.unselectedFields = this.memberFields
        .filter(
          (o) =>
            this.selectedFields.filter((f) => f.Key === o.Key).length === 0
        )
        .sort(this.compareFields);
    },
    changeEmployers(val) {
      this.$emit("change-employers", val);
    },
    scrollToValidationErrors() {
      // document.getElementById("validation-errors-box").scrollIntoView({
      //   behavior: "smooth",
      // });
      // window.scrollTo(0, 0);
      this.validate();
      if (this.validationErrors.length === 0) {
        this.color = "success";
        this.message = this.$t("views.planwizardview.validate_success_{0}", [
          this.generalSettings.UniqueIdentifier,
        ]);
        this.snackbar = true;
        this.valid.employers = true;
        this.valid.unionsLocals = true;
        this.valid.memberGroups = true;
        this.valid.billingImport = true;
        this.valid.coverageParameters = true;
      } else {
        window.scrollTo({ top: 0, behavior: "smooth" });
        console.log(this.panelsWithErrors);
        if (this.panelsWithErrors.indexOf("Unions") > -1) {
          this.valid.unionsLocals = false;
        } else {
          this.valid.unionsLocals = true;
        }
        if (this.panelsWithErrors.indexOf("Employers") > -1) {
          this.valid.employers = false;
        } else {
          this.valid.employers = true;
        }
        if (this.panelsWithErrors.indexOf("MemberGroups") > -1) {
          this.valid.memberGroups = false;
        } else {
          this.valid.memberGroups = true;
        }
        if (this.panelsWithErrors.indexOf("BillingImport") > -1) {
          this.valid.billingImport = false;
        } else {
          this.valid.billingImport = true;
        }
        if (this.panelsWithErrors.indexOf("CoverageParameters") > -1) {
          this.valid.coverageParameters = false;
        } else {
          this.valid.coverageParameters = true;
        }
      }
    },
    async validate() {
      if (this.validated) {
        this.panels = [0, 1, 2, 3, 4, 5, 6, 7]
        if (this.$refs.trustFund) {
          this.$refs.trustFund.isBankAccountValid();
        }
        // this.$nextTick(() => {
        // this.panels = [];
        this.bankAccountValid = true;
        console.log(this.bankAccount)
        if (this.bankAccount) {
          for (let key of Object.keys(this.bankAccount)) {
            if (this.bankAccount[key] === null || this.bankAccount[key] === "") {
              delete this.bankAccount[key];
            }
          }
          if (Object.keys(this.bankAccount).length > 0) {
            if (Object.keys(this.bankAccount).length === 3) {
              this.bankAccountValid = (await this.validateBankAccount({
                payload: {
                  InstitutionNumber: this.bankAccount.InstitutionNumber,
                  TransitNumber: this.bankAccount.TransitNumber,
                },
              })).data;
            } else {
              this.bankAccountValid = false;
            }
          }
        }
        const valid =
          this.valid.generalSettings &&
          this.valid.trustFund &&
          this.bankAccountValid;
        console.log("valid-change: ", this.valid.generalSettings, this.valid.trustFund, this.bankAccountValid)
        this.$emit("valid-changed", valid);
        let index = this.validationErrors.indexOf(
          "components.plan_builder.client_validation_errors"
        );
        if (valid) {
          if (index > -1) {
            this.validationErrors.splice(index, 1);
          }
        } else {
          if (index < 0) {
            this.validationErrors.unshift(
              "components.plan_builder.client_validation_errors"
            );
          }
        }
        // });
      }
    },
    unmask(v, mask) {
      if (v) {
        return masker(v, mask, false, tokens);
      }
      return v;
    },
    panelFocus(panelId) {
      let panel = document.getElementById(panelId);
      if (panel.ariaExpanded === "true") {
        return;
      }
      setTimeout(() => {
        document.getElementById(panelId).scrollIntoView({
          behavior: "smooth",
        });
      }, 450);
    },
    changeMemberFields(fields) {
      this.$emit("change-fields", fields);
      this.$nextTick(() => {
        this.loadBillingFields();
      });
    },
    async loadBillingFields() {
      try {
        this.loading = true;
        this.allBillingImportFields = (await this.getBillingImportFields({
          planIdentityId: this.planId,
          payload: this.selectedFields.map((o) => o.Key),
        })).data;

        for (let field of this.billingFields) {
          field.Label = field.Label.replace(" (optional)", "");
          field.Label = field.Label.replace(" (required)", "");
          if (field.IsOptional) {
            field.Label = field.Label + " (optional)";
          } else {
            field.Label = field.Label + " (required)";
          }
        }
        this.unselectedBillingFields = this.allBillingImportFields.filter(
          (o) => this.billingFields.filter((d) => d.Key === o.Key).length === 0
        );
      } catch (error) {
        this.error = error;
      } finally {
        this.loading = false;
        // this.validate();
      }
    },
  },
};
</script>
<style scoped>
.validation-error {
  color: #ff5252;
  font-size: 14px;
  display: block;
}
.validation-errors-box {
  background-color: whitesmoke;
  margin: 20px;
}
.validation-error-header {
  background-color: #ff5252;
  color: white;
}
</style>
